import posthog from 'posthog-js';

import { Store } from '@reduxjs/toolkit';
import { RootState } from '../redux/store';

const levelPriority: Record<string, number> = {
    error: 0,
    warn: 1,
    log: 2,
    info: 3,
    debug: 4,
    trace: 5,
};

const SENSITIVE_KEYS = [
    'name',
    'firstname',
    'lastname',
    'password',
    'avatar',
    'Token',
    'token',
    'email',
    'phone',
    'mobile',
    'access_token',
    'refresh_token',
    'authorization',
    'auth',
    'apikey',
    'api_key',
    'sessionid',
    'session_id',
    'address',
    'pin',
    'otp'
];

// JWT: three base64url segments separated by dots, header always starts with eyJ
const JWT_RE = /eyJ[A-Za-z0-9_-]+\.[A-Za-z0-9_-]+\.[A-Za-z0-9_-]*/g;
// E.164-ish phone numbers: optional +, 10-15 digits
const PHONE_RE = /\+?[0-9]{10,15}/g;

function maskString(value: string): string {
    return value
        .replace(JWT_RE, '[token]')
        .replace(PHONE_RE, '[phone]');
}

function maskSensitive(data: any): any {
    if (Array.isArray(data)) {
        return data.map(maskSensitive);
    } else if (data !== null && typeof data === 'object') {
        const clone: Record<string, any> = {};
        for (const key in data) {
            const loweredKey = key.toLowerCase();
            const value = data[key];

            if (SENSITIVE_KEYS.includes(loweredKey)) {
                if (loweredKey === 'email' && typeof value === 'string') {
                    clone[key] = value.replace(/(.{2}).+(@.+)/, (_: any, a: any, b: any) => `${a}***${b}`);
                } else {
                    clone[key] = '***';
                }
            } else {
                clone[key] = maskSensitive(value);
            }
        }
        return clone;
    }
    if (typeof data === 'string') {
        return maskString(data);
    }
    return data;
}


function shouldLog(level: keyof typeof levelPriority) {
    const currentLevel = process.env.REACT_APP_LOG_LEVEL || 'debug';
    return levelPriority[level] <= levelPriority[currentLevel];
}
export const Stdconsole = (store: Store<RootState>) => {
    const originalConsole = {
        log: console.log.bind(console),
        error: console.error.bind(console),
        warn: console.warn.bind(console),
        info: console.info.bind(console),
        debug: console.debug.bind(console),
        trace: console.trace.bind(console)
    };

    (['error', 'warn', 'log', 'info', 'debug', 'trace'] as const).forEach((level) => {
        console[level] = (...args: any[]) => {
            if (!shouldLog(level)) return;

            const time = new Date().toISOString();
            const maskedArgs = args.map((arg) =>
                typeof arg === 'object' ? maskSensitive(arg) : arg
            );

            const msg = `[${time}] [${level.toUpperCase()}]`;

            const userId = store.getState().userData.id;

            originalConsole[level](msg, ...maskedArgs);

            posthog.capture(`${level} logged`, {
                timestamp: time,
                level,
                userId: userId || 'anonymous',
                message: maskedArgs.map((arg) =>
                    typeof arg === 'object' ? JSON.stringify(arg) : String(arg)
                ).join(' ')
            });
        };
    });
};

